(() => {
  const OTF = window.OTF;
  if (!OTF) return;

  const MOCK_AIRPORTS = [
    { city: 'Casablanca', name: 'Mohammed V International Airport', iata: 'CMN', country: 'Morocco' },
    { city: 'Paris', name: 'Charles de Gaulle Airport', iata: 'CDG', country: 'France' },
    { city: 'Paris', name: 'Orly Airport', iata: 'ORY', country: 'France' },
    { city: 'New York', name: 'John F. Kennedy International Airport', iata: 'JFK', country: 'United States' },
    { city: 'New York', name: 'Newark Liberty International Airport', iata: 'EWR', country: 'United States' },
    { city: 'London', name: 'Heathrow Airport', iata: 'LHR', country: 'United Kingdom' },
    { city: 'Dubai', name: 'Dubai International Airport', iata: 'DXB', country: 'United Arab Emirates' },
    { city: 'Istanbul', name: 'Istanbul Airport', iata: 'IST', country: 'Türkiye' },
    { city: 'Madrid', name: 'Adolfo Suárez Madrid–Barajas Airport', iata: 'MAD', country: 'Spain' },
    { city: 'Rome', name: 'Leonardo da Vinci–Fiumicino Airport', iata: 'FCO', country: 'Italy' },
    { city: 'Lisbon', name: 'Humberto Delgado Airport', iata: 'LIS', country: 'Portugal' },
    { city: 'Marrakesh', name: 'Marrakesh Menara Airport', iata: 'RAK', country: 'Morocco' },
    { city: 'Rabat', name: 'Rabat–Salé Airport', iata: 'RBA', country: 'Morocco' },
    { city: 'Dakar', name: 'Blaise Diagne International Airport', iata: 'DSS', country: 'Senegal' },
    { city: 'Montreal', name: 'Montréal–Trudeau International Airport', iata: 'YUL', country: 'Canada' }
  ];

  const getStrings = (form) => ({
    originRequired: form.dataset.originRequired || 'Choose a departure city or airport.',
    destinationRequired: form.dataset.destinationRequired || 'Choose a destination city or airport.',
    sameAirport: form.dataset.sameAirport || 'Departure and destination must be different.',
    departureRequired: form.dataset.departureRequired || 'Choose a departure date.',
    returnRequired: form.dataset.returnRequired || 'Choose a return date.',
    returnBeforeDeparture: form.dataset.returnBeforeDeparture || 'Return date cannot be before departure date.',
    passengerRequired: form.dataset.passengerRequired || 'Add at least one passenger.',
    maximumPassengers: form.dataset.maximumPassengers || 'A maximum of 6 passengers is allowed.',
    airportSelectionRequired: form.dataset.airportSelectionRequired || 'Select an airport from the suggestions.'
  });

  const airportLabel = (airport) => {
    const place = [airport.city, airport.name].filter(Boolean).join(' — ');
    return `${place || airport.iata} (${airport.iata})`;
  };

  const normalizeSearchText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const countryDisplayNames = typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames([OTF.config.locale || 'en'], { type: 'region' })
    : null;

  const localizeCountry = (value) => {
    const source = String(value || '').trim();
    const regionalIndicators = [...source].filter((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint >= 0x1F1E6 && codePoint <= 0x1F1FF;
    }).slice(0, 2);
    if (regionalIndicators.length !== 2 || !countryDisplayNames) return source;
    const regionCode = regionalIndicators
      .map((character) => String.fromCharCode(character.codePointAt(0) - 0x1F1E6 + 65))
      .join('');
    try {
      return `${regionalIndicators.join('')} ${countryDisplayNames.of(regionCode)}`;
    } catch (error) {
      return source;
    }
  };

  const normalizeAirport = (airport) => {
    const iata = String(airport.iata || airport.iata_code || '').toUpperCase();
    const rawName = airport.name || airport.airport_name || airport.airport || airport.label || '';
    const name = String(rawName).replace(new RegExp(`\\s*\\(${iata}\\)\\s*$`, 'i'), '').trim();
    const originalCountry = airport.country || airport.country_name || '';
    const normalized = {
      city: airport.city || airport.city_name || airport.municipality || '',
      name,
      iata,
      country: localizeCountry(originalCountry)
    };
    normalized.searchText = normalizeSearchText([
      normalized.iata,
      normalized.city,
      normalized.name,
      normalized.country,
      originalCountry,
      airport.label,
      ...(Array.isArray(airport.search_iata) ? airport.search_iata : [])
    ].filter(Boolean).join(' '));
    return normalized;
  };

  let airportDataPromise;
  const loadAirportData = async () => {
    if (!airportDataPromise) {
      airportDataPromise = fetch(OTF.config.airportDataUrl, {
        headers: { Accept: 'application/json' },
        cache: 'force-cache'
      })
        .then((response) => {
          if (!response.ok) throw new Error('Airport data could not be loaded');
          return response.json();
        })
        .then((payload) => (Array.isArray(payload) ? payload : payload.data || payload.airports || []))
        .then((airports) => airports.map(normalizeAirport).filter((airport) => airport.iata));
    }
    return airportDataPromise;
  };

  const airportMatchRank = (airport, term) => {
    const iata = normalizeSearchText(airport.iata);
    const city = normalizeSearchText(airport.city);
    const name = normalizeSearchText(airport.name);
    if (iata === term) return 0;
    if (iata.startsWith(term)) return 1;
    if (city === term) return 2;
    if (city.startsWith(term)) return 3;
    if (name.startsWith(term)) return 4;
    if (airport.searchText.split(/\s+/).some((word) => word.startsWith(term))) return 5;
    return 6;
  };

  const searchAirports = async (query) => {
    const term = normalizeSearchText(query);
    try {
      const airports = await loadAirportData();
      return airports
        .filter((airport) => airport.searchText.includes(term))
        .sort((a, b) => airportMatchRank(a, term) - airportMatchRank(b, term) || a.city.localeCompare(b.city))
        .slice(0, 8);
    } catch (error) {
      return MOCK_AIRPORTS
        .map(normalizeAirport)
        .filter((airport) => airport.searchText.includes(term))
        .sort((a, b) => airportMatchRank(a, term) - airportMatchRank(b, term))
        .slice(0, 8);
    }
  };

  const setupAirportInput = (form, input) => {
    const kind = input.dataset.airportInput;
    const codeInput = form.querySelector(`[data-airport-code="${kind}"]`);
    const suggestions = form.querySelector(`[data-airport-suggestions="${kind}"]`);
    const clearButton = form.querySelector(`[data-airport-clear="${kind}"]`);
    if (!suggestions || !codeInput) return;

    let activeRequest = 0;
    const syncClearButton = () => { if (clearButton) clearButton.hidden = !input.value.trim(); };
    const clearAirportData = () => {
      codeInput.value = '';
      delete input.dataset.airportCity;
      delete input.dataset.airportName;
      delete input.dataset.airportCountry;
    };
    const closeSuggestions = () => {
      suggestions.hidden = true;
      suggestions.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };
    const chooseAirport = (airport) => {
      input.value = airportLabel(airport);
      codeInput.value = airport.iata;
      input.dataset.airportCity = airport.city;
      input.dataset.airportName = airport.name;
      input.dataset.airportCountry = airport.country;
      syncClearButton();
      closeSuggestions();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const renderSuggestions = (airports) => {
      suggestions.replaceChildren();
      airports.forEach((airport, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'airport-suggestion';
        button.id = `${suggestions.id}-option-${index}`;
        button.setAttribute('role', 'option');
        const text = document.createElement('span');
        const city = document.createElement('strong');
        city.textContent = airport.city || airport.name || airport.iata;
        const detail = document.createElement('small');
        detail.textContent = [airport.city ? airport.name : '', airport.country].filter(Boolean).join(' · ');
        const code = document.createElement('strong');
        code.textContent = airport.iata;
        text.append(city, detail);
        button.append(text, code);
        button.addEventListener('click', () => chooseAirport(airport));
        suggestions.appendChild(button);
      });
      suggestions.hidden = airports.length === 0;
      input.setAttribute('aria-expanded', String(airports.length > 0));
    };
    const handleInput = OTF.debounce(async () => {
      const requestId = ++activeRequest;
      if (input.value.trim().length < 2) { closeSuggestions(); return; }
      try {
        const airports = await searchAirports(input.value);
        if (requestId === activeRequest) renderSuggestions(airports);
      } catch (error) { if (requestId === activeRequest) closeSuggestions(); }
    }, 280);
    input.addEventListener('input', () => {
      clearAirportData();
      syncClearButton();
      handleInput();
    });
    input.addEventListener('keydown', (event) => {
      const options = [...suggestions.querySelectorAll('[role="option"]')];
      const current = options.findIndex((option) => option.id === input.getAttribute('aria-activedescendant'));
      if (event.key === 'Escape') { closeSuggestions(); return; }
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current <= 0 ? options.length - 1 : current - 1);
        input.setAttribute('aria-activedescendant', options[next].id);
        options[next].scrollIntoView({ block: 'nearest' });
      }
      if (event.key === 'Enter' && current >= 0) { event.preventDefault(); options[current].click(); }
    });
    clearButton?.addEventListener('click', () => {
      activeRequest += 1;
      input.value = '';
      clearAirportData();
      syncClearButton();
      closeSuggestions();
      input.focus();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    document.addEventListener('click', (event) => { if (!suggestions.contains(event.target) && event.target !== input) closeSuggestions(); });
    syncClearButton();
  };

  const serializeForm = (form) => {
    const data = new FormData(form);
    const originInput = form.querySelector('[data-airport-input="origin"]');
    const destinationInput = form.querySelector('[data-airport-input="destination"]');
    const passengers = {
      adults: Number(data.get('adults') || 0),
      children: Number(data.get('children') || 0),
      infants: Number(data.get('infants') || 0)
    };
    return {
      tripType: data.get('tripType') || 'roundtrip',
      origin: {
        display: data.get('origin') || '',
        city: originInput?.dataset.airportCity || String(data.get('origin') || '').split('—')[0].trim(),
        name: originInput?.dataset.airportName || '',
        iata: String(data.get('origin_iata') || '').toUpperCase() || (String(data.get('origin') || '').match(/\(([A-Z]{3})\)/)?.[1] || ''),
        country: originInput?.dataset.airportCountry || ''
      },
      destination: {
        display: data.get('destination') || '',
        city: destinationInput?.dataset.airportCity || String(data.get('destination') || '').split('—')[0].trim(),
        name: destinationInput?.dataset.airportName || '',
        iata: String(data.get('destination_iata') || '').toUpperCase() || (String(data.get('destination') || '').match(/\(([A-Z]{3})\)/)?.[1] || ''),
        country: destinationInput?.dataset.airportCountry || ''
      },
      departureDate: String(data.get('departureDate') || ''),
      returnDate: data.get('tripType') === 'roundtrip' ? String(data.get('returnDate') || '') : '',
      passengers,
      selectionMode: '',
      outboundFlight: null,
      returnFlight: null,
      selectedItineraryId: '',
      flightProvider: null,
      passengerDetails: [],
      addons: [],
      reservationId: ''
    };
  };

  const populateForm = (form, state) => {
    if (!state || !state.origin) return;
    const trip = form.querySelector(`[name="tripType"][value="${state.tripType || 'roundtrip'}"]`);
    if (trip) { trip.checked = true; trip.dispatchEvent(new Event('change', { bubbles: true })); }
    ['origin', 'destination'].forEach((kind) => {
      const input = form.querySelector(`[data-airport-input="${kind}"]`);
      const code = form.querySelector(`[data-airport-code="${kind}"]`);
      if (!input || !state[kind]) return;
      input.value = state[kind].display || `${state[kind].city || ''}${state[kind].iata ? ` (${state[kind].iata})` : ''}`;
      input.dataset.airportCity = state[kind].city || '';
      input.dataset.airportName = state[kind].name || '';
      input.dataset.airportCountry = state[kind].country || '';
      if (code) code.value = state[kind].iata || '';
    });
    const departure = form.querySelector('[data-departure-date]');
    const returning = form.querySelector('[data-return-date]');
    if (departure) departure.value = state.departureDate || '';
    if (returning) returning.value = state.returnDate || '';
    Object.entries(state.passengers || {}).forEach(([kind, value]) => {
      const input = form.querySelector(`[data-passenger-count="${kind}"]`);
      if (input) input.value = value;
    });
    form.querySelectorAll('[data-airport-clear]').forEach((button) => {
      const input = form.querySelector(`[data-airport-input="${button.dataset.airportClear}"]`);
      button.hidden = !input?.value.trim();
    });
    updatePassengerSummary(form);
  };

  const updatePassengerSummary = (form) => {
    const inputs = [...form.querySelectorAll('[data-passenger-count]')];
    const total = inputs.reduce((sum, input) => sum + Number(input.value || 0), 0);
    const summary = form.querySelector('[data-passenger-summary]');
    if (summary) summary.textContent = `${total} ${total === 1 ? OTF.config.strings.passengerSingular : OTF.config.strings.passengerPlural}`;
    const adults = form.querySelector('[data-passenger-count="adults"]');
    const infants = form.querySelector('[data-passenger-count="infants"]');
    form.querySelectorAll('[data-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('input');
      const minus = stepper.querySelector('[data-stepper-minus]');
      const plus = stepper.querySelector('[data-stepper-plus]');
      const minimum = input.dataset.passengerCount === 'adults' ? 1 : 0;
      if (minus) minus.disabled = Number(input.value) <= minimum;
      if (plus) plus.disabled = total >= 6 || (input === infants && Number(infants.value) >= Number(adults.value));
    });
    return total;
  };

  const clearErrors = (form) => {
    form.querySelectorAll('.is-invalid').forEach((element) => element.classList.remove('is-invalid'));
    form.querySelectorAll('[data-error-for]').forEach((element) => { element.textContent = ''; });
    const summary = form.querySelector('[data-form-error]');
    if (summary) { summary.hidden = true; summary.textContent = ''; }
  };

  const validateForm = (form, state) => {
    clearErrors(form);
    const strings = getStrings(form);
    const errors = [];
    const setError = (name, message, selector) => {
      errors.push(message);
      const node = form.querySelector(`[data-error-for="${name}"]`);
      const input = form.querySelector(selector);
      if (node) node.textContent = message;
      if (input) input.classList.add('is-invalid');
    };
    if (!state.origin.display.trim()) setError('origin', strings.originRequired, '[data-airport-input="origin"]');
    if (!state.destination.display.trim()) setError('destination', strings.destinationRequired, '[data-airport-input="destination"]');
    if (!OTF.config.useMockFlights && state.origin.display.trim() && !state.origin.iata) setError('origin', strings.airportSelectionRequired, '[data-airport-input="origin"]');
    if (!OTF.config.useMockFlights && state.destination.display.trim() && !state.destination.iata) setError('destination', strings.airportSelectionRequired, '[data-airport-input="destination"]');
    const originKey = state.origin.iata || state.origin.display.trim().toLowerCase();
    const destinationKey = state.destination.iata || state.destination.display.trim().toLowerCase();
    if (originKey && destinationKey && originKey === destinationKey) setError('destination', strings.sameAirport, '[data-airport-input="destination"]');
    if (!state.departureDate) setError('departureDate', strings.departureRequired, '[data-departure-date]');
    if (state.tripType === 'roundtrip' && !state.returnDate) setError('returnDate', strings.returnRequired, '[data-return-date]');
    if (state.departureDate && state.returnDate && state.returnDate < state.departureDate) setError('returnDate', strings.returnBeforeDeparture, '[data-return-date]');
    const total = Object.values(state.passengers).reduce((sum, value) => sum + Number(value), 0);
    if (total < 1) setError('passengers', strings.passengerRequired, '[data-passenger-trigger]');
    if (total > 6) setError('passengers', strings.maximumPassengers, '[data-passenger-trigger]');
    const summary = form.querySelector('[data-form-error]');
    if (errors.length && summary) { summary.textContent = errors[0]; summary.hidden = false; }
    return errors.length === 0;
  };

  const setupForm = (form) => {
    const today = new Date();
    today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
    const todayValue = today.toISOString().slice(0, 10);
    const departure = form.querySelector('[data-departure-date]');
    const returning = form.querySelector('[data-return-date]');
    if (departure) departure.min = todayValue;
    if (returning) returning.min = todayValue;

    form.querySelectorAll('[data-date-control]').forEach((control) => {
      const input = control.querySelector('input[type="date"]');
      if (!input) return;
      control.addEventListener('click', () => {
        input.focus({ preventScroll: true });
        if (typeof input.showPicker === 'function') {
          try { input.showPicker(); } catch (error) { /* Native picker fallback remains available. */ }
        }
      });
    });

    form.querySelectorAll('[name="tripType"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const isRoundTrip = form.querySelector('[name="tripType"]:checked')?.value === 'roundtrip';
        form.classList.toggle('flight-search--oneway', !isRoundTrip);
        form.dataset.tripType = isRoundTrip ? 'roundtrip' : 'oneway';
        const returnField = form.querySelector('[data-return-field]');
        if (returnField) returnField.hidden = !isRoundTrip;
        if (returning) { returning.required = isRoundTrip; returning.disabled = !isRoundTrip; if (!isRoundTrip) returning.value = ''; }
      });
    });
    departure?.addEventListener('change', () => {
      if (!returning) return;
      returning.min = departure.value || todayValue;
      if (returning.value && returning.value < departure.value) returning.value = departure.value;
    });

    form.querySelectorAll('[data-airport-input]').forEach((input) => setupAirportInput(form, input));
    form.querySelector('[data-swap-airports]')?.addEventListener('click', () => {
      const origin = form.querySelector('[data-airport-input="origin"]');
      const destination = form.querySelector('[data-airport-input="destination"]');
      const originCode = form.querySelector('[data-airport-code="origin"]');
      const destinationCode = form.querySelector('[data-airport-code="destination"]');
      if (!origin || !destination) return;
      const originData = { value: origin.value, city: origin.dataset.airportCity, name: origin.dataset.airportName, country: origin.dataset.airportCountry, code: originCode?.value };
      origin.value = destination.value; origin.dataset.airportCity = destination.dataset.airportCity || ''; origin.dataset.airportName = destination.dataset.airportName || ''; origin.dataset.airportCountry = destination.dataset.airportCountry || '';
      destination.value = originData.value; destination.dataset.airportCity = originData.city || ''; destination.dataset.airportName = originData.name || ''; destination.dataset.airportCountry = originData.country || '';
      if (originCode && destinationCode) { originCode.value = destinationCode.value; destinationCode.value = originData.code || ''; }
      form.querySelectorAll('[data-airport-clear]').forEach((button) => { button.hidden = !form.querySelector(`[data-airport-input="${button.dataset.airportClear}"]`)?.value.trim(); });
    });

    const trigger = form.querySelector('[data-passenger-trigger]');
    const popover = form.querySelector('[data-passenger-popover]');
    const closePopover = () => { if (!popover || !trigger) return; popover.hidden = true; trigger.setAttribute('aria-expanded', 'false'); };
    trigger?.addEventListener('click', () => {
      if (!popover) return;
      const open = !popover.hidden;
      popover.hidden = open;
      trigger.setAttribute('aria-expanded', String(!open));
    });
    form.querySelector('[data-passenger-done]')?.addEventListener('click', closePopover);
    document.addEventListener('click', (event) => { if (popover && trigger && !popover.contains(event.target) && !trigger.contains(event.target)) closePopover(); });
    form.querySelectorAll('[data-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('input');
      stepper.querySelector('[data-stepper-minus]')?.addEventListener('click', () => { input.value = Math.max(input.dataset.passengerCount === 'adults' ? 1 : 0, Number(input.value) - 1); updatePassengerSummary(form); });
      stepper.querySelector('[data-stepper-plus]')?.addEventListener('click', () => { if (updatePassengerSummary(form) < 6) input.value = Number(input.value) + 1; updatePassengerSummary(form); });
    });
    updatePassengerSummary(form);

    const priorState = OTF.getBookingState();
    if (priorState.origin) populateForm(form, priorState);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const state = serializeForm(form);
      if (!validateForm(form, state)) return;
      OTF.setBookingState(state);
      const button = form.querySelector('[data-search-submit]');
      const label = form.querySelector('[data-button-label]');
      const spinner = form.querySelector('[data-button-spinner]');
      if (button) button.disabled = true;
      if (label) label.textContent = OTF.config.strings.searching;
      if (spinner) spinner.hidden = false;
      const params = new URLSearchParams({
        tripType: state.tripType,
        origin: state.origin.iata || state.origin.display,
        originCity: state.origin.city,
        destination: state.destination.iata || state.destination.display,
        destinationCity: state.destination.city,
        departureDate: state.departureDate,
        returnDate: state.returnDate,
        adults: state.passengers.adults,
        children: state.passengers.children,
        infants: state.passengers.infants
      });
      window.location.assign(`${OTF.config.routes.searchResults}?${params.toString()}`);
    });
  };

  document.querySelectorAll('[data-flight-search]').forEach(setupForm);
})();
